import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LibraryDocModel } from '@app/core/model/library-doc.model';
import {
  TSpecBodyAddLibraryDoc,
  TSpecRoute_addLibraryDoc
} from '@backend/types/generated/Library/addLibraryDoc';
import {
  TSpecQueryGetAllLibraryDocs,
  TSpecRoute_getAllLibraryDocs
} from '@backend/types/generated/Library/getAllLibraryDocs';
import {
  TSpecPathGetLibraryDoc,
  TSpecRoute_getLibraryDoc
} from '@backend/types/generated/Library/getLibraryDoc';
import {
  TSpecPathGetLibraryDocGroup,
  TSpecRoute_getLibraryDocGroup
} from '@backend/types/generated/Library/getLibraryDocGroup';
import {
  TSpecPathReadLibraryDoc,
  TSpecRoute_readLibraryDoc
} from '@backend/types/generated/Library/readLibraryDoc';
import {
  TSpecBodyReorderLibraryDocs,
  TSpecRoute_reorderLibraryDocs
} from '@backend/types/generated/Library/reorderLibraryDocs';
import {
  TSpecBodyRequestUsersToSignLibraryDoc,
  TSpecPathRequestUsersToSignLibraryDoc,
  TSpecRoute_requestUsersToSignLibraryDoc
} from '@backend/types/generated/Library/requestUsersToSignLibraryDoc';
import {
  TSpecBodySaveAsNewVersion,
  TSpecPathSaveAsNewVersion,
  TSpecRoute_saveAsNewVersion
} from '@backend/types/generated/Library/saveAsNewVersion';
import {
  TSpecPathSignLibraryDoc,
  TSpecRoute_signLibraryDoc
} from '@backend/types/generated/Library/signLibraryDoc';
import {
  TSpecPathSoftDeleteLibraryDoc,
  TSpecRoute_softDeleteLibraryDoc
} from '@backend/types/generated/Library/softDeleteLibraryDoc';
import {
  TSpecBodySubmitLibraryDocFeedback,
  TSpecPathSubmitLibraryDocFeedback,
  TSpecRoute_submitLibraryDocFeedback
} from '@backend/types/generated/Library/submitLibraryDocFeedback';
import {
  TSpecBodyUpdateLibraryDoc,
  TSpecPathUpdateLibraryDoc,
  TSpecRoute_updateLibraryDoc
} from '@backend/types/generated/Library/updateLibraryDoc';
import { Observable } from 'rxjs';

@Injectable()
export class LibraryApiService {
  public constructor(private readonly _httpClient: HttpClient) {}

  public getAllLibraryDocs(params: {
    query: TSpecQueryGetAllLibraryDocs;
  }): Observable<
    {
      originalDoc: LibraryDocModel;
      versions: LibraryDocModel[];
      latestVersion: LibraryDocModel | null;
    }[]
  > {
    return this._httpClient.get<
      {
        originalDoc: LibraryDocModel;
        versions: LibraryDocModel[];
        latestVersion: LibraryDocModel | null;
      }[]
    >(TSpecRoute_getAllLibraryDocs(), { params: { ...params.query } });
  }

  public getLibraryDoc(params: {
    path: TSpecPathGetLibraryDoc;
  }): Observable<LibraryDocModel> {
    return this._httpClient.get<LibraryDocModel>(
      TSpecRoute_getLibraryDoc(params.path)
    );
  }

  public getLibraryDocGroup(libraryDocId: string): Observable<{
    originalDoc: LibraryDocModel;
    versions: LibraryDocModel[];
    latestVersion: LibraryDocModel | null;
  }> {
    return this._httpClient.get<{
      originalDoc: LibraryDocModel;
      versions: LibraryDocModel[];
      latestVersion: LibraryDocModel | null;
    }>(
      TSpecRoute_getLibraryDocGroup({
        libraryDocId
      } as TSpecPathGetLibraryDocGroup)
    );
  }

  public addLibraryDoc(params: {
    body: TSpecBodyAddLibraryDoc;
  }): Observable<LibraryDocModel> {
    return this._httpClient.post<LibraryDocModel>(
      TSpecRoute_addLibraryDoc(),
      params.body
    );
  }

  public updateLibraryDoc(params: {
    path: TSpecPathUpdateLibraryDoc;
    body: TSpecBodyUpdateLibraryDoc;
  }): Observable<LibraryDocModel> {
    return this._httpClient.put<LibraryDocModel>(
      TSpecRoute_updateLibraryDoc(params.path),
      params.body
    );
  }

  public saveAsNewVersion(params: {
    path: TSpecPathSaveAsNewVersion;
    body: TSpecBodySaveAsNewVersion;
  }): Observable<LibraryDocModel> {
    return this._httpClient.post<LibraryDocModel>(
      TSpecRoute_saveAsNewVersion(params.path),
      params.body
    );
  }

  public softDeleteLibraryDoc(params: { path: TSpecPathSoftDeleteLibraryDoc }) {
    return this._httpClient.delete(
      TSpecRoute_softDeleteLibraryDoc(params.path)
    );
  }

  public signLibraryDoc(params: {
    path: TSpecPathSignLibraryDoc;
  }): Observable<LibraryDocModel> {
    return this._httpClient.post<LibraryDocModel>(
      TSpecRoute_signLibraryDoc(params.path),
      {}
    );
  }

  public readLibraryDoc(params: {
    path: TSpecPathReadLibraryDoc;
  }): Observable<LibraryDocModel> {
    return this._httpClient.post<LibraryDocModel>(
      TSpecRoute_readLibraryDoc(params.path),
      {}
    );
  }

  public requestUsersToSignLibraryDoc(params: {
    path: TSpecPathRequestUsersToSignLibraryDoc;
    body: TSpecBodyRequestUsersToSignLibraryDoc;
  }) {
    return this._httpClient.post(
      TSpecRoute_requestUsersToSignLibraryDoc(params.path),
      params.body
    );
  }

  public submitLibraryDocFeedback(params: {
    path: TSpecPathSubmitLibraryDocFeedback;
    body: TSpecBodySubmitLibraryDocFeedback;
  }) {
    return this._httpClient.post(
      TSpecRoute_submitLibraryDocFeedback(params.path),
      params.body
    );
  }

  public reorderLibraryDocs(params: { body: TSpecBodyReorderLibraryDocs }) {
    return this._httpClient.post<void>(
      TSpecRoute_reorderLibraryDocs(),
      params.body
    );
  }
}
